// 渲染工具：模型/分组/状态/时间等 tag
// Deep Space Protocol 统一 Pill：border-0 / rounded-md / 自定义语义色
// 展示类渲染：与内部参考实现 helpers/render 行为对齐
import { Tag, Progress, Tooltip } from '@arco-design/web-react'
import type { ReactNode } from 'react'
import {
  TOKEN_STATUS_TEXT,
  USER_ROLE_TEXT,
  CHANNEL_STATUS_TEXT,
  LOG_TYPE_TEXT,
  CHANNEL_TYPE_MAP,
} from './constants'
import { renderQuota, renderQuotaCompact } from './quota'
import i18n from '@/i18n'
import { getDateLocale } from './locale'

// ===================== Pill 基类 =====================

export type PillVariant =
  | 'success' // 激活 / 成功（Emerald）
  | 'warning' // 待处理 / 警告（Amber）
  | 'danger' //  失败 / 错误（Red）
  | 'neutral' // 禁用 / 灰色（Gray）
  | 'info' //    信息 / 蓝色（Indigo）
  | 'cyan' //    进行中（Cyan）
  | 'violet' //  紫色（Violet）
  | 'rose' //    玫红
  | 'orange' //  橙色（兼容旧有 orange 用法）

const VARIANT_STYLES: Record<PillVariant, { bg: string; color: string }> = {
  success: { bg: '#D1FAE5', color: '#065F46' },
  warning: { bg: '#FEF3C7', color: '#92400E' },
  danger: { bg: '#FEE2E2', color: '#991B1B' },
  neutral: { bg: '#F3F4F6', color: '#4B5563' },
  info: { bg: '#EEF2FF', color: '#4338CA' },
  cyan: { bg: '#CFFAFE', color: '#164E63' },
  violet: { bg: '#EDE9FE', color: '#6D28D9' },
  rose: { bg: '#FFE4E6', color: '#9F1239' },
  orange: { bg: '#FFEDD5', color: '#9A3412' },
}

interface PillProps {
  variant?: PillVariant
  children: ReactNode
  className?: string
  title?: string
}

/**
 * 语义化 Pill —— 全站 Tag 唯一入口。
 * 使用 Arco Tag 作为容器，覆盖其颜色为 Deep Space Protocol 语义色。
 */
export function Pill({ variant = 'neutral', children, className = '', title }: PillProps) {
  const style = VARIANT_STYLES[variant]
  return (
    <Tag
      size="small"
      title={title}
      className={`!border-0 !rounded-md !px-2 !py-0.5 !text-xs !font-medium ${className}`}
      style={{ background: style.bg, color: style.color }}
    >
      {children}
    </Tag>
  )
}

// ===================== Token 状态 =====================

export function renderTokenStatus(status: number) {
  const variant: PillVariant =
    status === 1 ? 'success' : status === 2 ? 'danger' : status === 3 ? 'warning' : 'orange'
  return <Pill variant={variant}>{i18n.t(TOKEN_STATUS_TEXT[status] || '未知')}</Pill>
}

// ===================== User 角色 / 状态 =====================

export function renderRole(role: number) {
  const variant: PillVariant = role >= 100 ? 'danger' : role >= 10 ? 'warning' : 'info'
  return <Pill variant={variant}>{i18n.t(USER_ROLE_TEXT[role] || '未知')}</Pill>
}

export function renderUserStatus(status: number) {
  return status === 1 ? (
    <Pill variant="success">{i18n.t('已启用')}</Pill>
  ) : (
    <Pill variant="danger">{i18n.t('已禁用')}</Pill>
  )
}

// ===================== Channel 状态 / 类型 =====================

export function renderChannelStatus(status: number) {
  const variant: PillVariant =
    status === 1 ? 'success' : status === 2 ? 'danger' : status === 3 ? 'warning' : 'neutral'
  return <Pill variant={variant}>{i18n.t(CHANNEL_STATUS_TEXT[status] || '未知')}</Pill>
}

export function renderChannelType(type: number) {
  return <Pill variant="info">{CHANNEL_TYPE_MAP[type] || i18n.t('自定义')}</Pill>
}

// ===================== Log 类型 =====================

export function renderLogType(type: number) {
  const map: Record<number, PillVariant> = {
    1: 'success',
    2: 'info',
    3: 'violet',
    4: 'neutral',
    5: 'danger',
    6: 'warning',
  }
  return <Pill variant={map[type] || 'neutral'}>{i18n.t(LOG_TYPE_TEXT[type] || '未知')}</Pill>
}

// ===================== 模型 / 分组 Tag =====================

export function renderModelTag(model: string) {
  const modelLower = (model || '').toLowerCase()
  let variant: PillVariant = 'info'
  if (modelLower.includes('gpt-4')) variant = 'success'
  else if (modelLower.includes('gpt-3')) variant = 'cyan'
  else if (modelLower.includes('claude')) variant = 'violet'
  else if (modelLower.includes('gemini')) variant = 'warning'
  else if (modelLower.includes('deepseek')) variant = 'info'
  else if (modelLower.includes('qwen')) variant = 'danger'
  else if (modelLower.includes('moonshot') || modelLower.includes('kimi')) variant = 'violet'
  else if (modelLower.includes('doubao')) variant = 'cyan'
  else if (modelLower.includes('glm')) variant = 'rose'
  else if (modelLower.includes('grok')) variant = 'neutral'
  else if (modelLower.includes('mj') || modelLower.includes('midjourney')) variant = 'rose'
  return (
    <Pill key={model} variant={variant} className="mr-1 mb-1">
      {model}
    </Pill>
  )
}

export function renderModelTags(models: string[] | string | undefined) {
  const arr = Array.isArray(models)
    ? models
    : typeof models === 'string'
      ? models
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : []
  if (!arr.length) return <span className="text-gray-400 text-xs">-</span>
  return <div className="flex flex-wrap">{arr.map((m) => renderModelTag(m))}</div>
}

const MODEL_FEATURE_TAG_TEXT: Record<string, string> = {
  reasoning: '推理',
  tools: '工具调用',
  files: '文件处理',
  'open weights': '开源权重',
  openweights: '开源权重',
  vision: '多模态',
  multimodal: '多模态',
  audio: '音频',
  image: '图像',
  embedding: '向量',
  embeddings: '向量',
}

const MODEL_FEATURE_TAG_VARIANT: Record<string, PillVariant> = {
  reasoning: 'violet',
  tools: 'cyan',
  files: 'orange',
  'open weights': 'success',
  openweights: 'success',
  vision: 'info',
  multimodal: 'info',
  audio: 'rose',
  image: 'warning',
  embedding: 'neutral',
  embeddings: 'neutral',
}

const MODEL_FEATURE_TAG_ALIASES: Record<string, string> = {
  tool: 'tools',
  'function calling': 'tools',
  functioncalling: 'tools',
  function_calling: 'tools',
  functioncall: 'tools',
  file: 'files',
  'open weight': 'open weights',
  'open-weight': 'open weights',
  'open-weights': 'open weights',
  open_weight: 'open weights',
  open_weights: 'open weights',
  multi_modal: 'multimodal',
  'multi modal': 'multimodal',
  'multi-modal': 'multimodal',
  multimodality: 'multimodal',
}

export function normalizeModelFeatureTagKey(tag: string) {
  const raw = tag.trim().toLowerCase()
  const spaced = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')
  return MODEL_FEATURE_TAG_ALIASES[raw] || MODEL_FEATURE_TAG_ALIASES[spaced] || spaced
}

export function splitModelFeatureTags(tags: string[] | string | undefined) {
  return Array.isArray(tags)
    ? tags.map((tag) => tag.trim()).filter(Boolean)
    : typeof tags === 'string'
      ? tags
          .split(/[,;|]+/)
          .map((tag) => tag.trim())
          .filter(Boolean)
      : []
}

export function normalizeModelFeatureTag(tag: string) {
  const key = normalizeModelFeatureTagKey(tag)
  const text = MODEL_FEATURE_TAG_TEXT[key]
  return text ? i18n.t(text) : tag.trim()
}

export function renderModelFeatureTags(tags: string[] | string | undefined, maxDisplay?: number) {
  const arr = splitModelFeatureTags(tags)
  if (!arr.length) return <span className="text-xs text-gray-400">-</span>
  const shown = typeof maxDisplay === 'number' ? arr.slice(0, maxDisplay) : arr
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((tag) => {
        const key = normalizeModelFeatureTagKey(tag)
        return (
          <Pill key={tag} variant={MODEL_FEATURE_TAG_VARIANT[key] || 'neutral'} title={tag}>
            {normalizeModelFeatureTag(tag)}
          </Pill>
        )
      })}
      {typeof maxDisplay === 'number' && arr.length > maxDisplay && (
        <Pill variant="neutral">+{arr.length - maxDisplay}</Pill>
      )}
    </div>
  )
}

export function renderGroupTag(group: string) {
  const g = group?.toLowerCase() || ''
  let variant: PillVariant = 'neutral'
  if (g === 'default') variant = 'info'
  else if (g === 'vip') variant = 'warning'
  else if (g === 'svip') variant = 'danger'
  else if (g === 'auto') variant = 'success'
  return (
    <Pill key={group} variant={variant} className="mr-1">
      {group}
    </Pill>
  )
}

export function renderGroupTags(groups: string[] | string | undefined) {
  if (!groups) return <span className="text-gray-400 text-xs">-</span>
  const arr = Array.isArray(groups)
    ? groups
    : typeof groups === 'string'
      ? groups.split(',').filter(Boolean)
      : []
  if (arr.length === 0) return <span className="text-gray-400 text-xs">-</span>
  return <div className="flex flex-wrap">{arr.map((g) => renderGroupTag(g))}</div>
}

// ===================== 时间 / 耗时 =====================

export function renderTimestamp(ts: number | undefined | null) {
  if (!ts) return <span className="text-gray-400">-</span>
  const date = new Date(ts * 1000)
  return <span className="text-xs text-gray-600">{date.toLocaleString(getDateLocale(), { hour12: false })}</span>
}

export function renderUseTime(seconds: number | undefined | null) {
  if (seconds == null || seconds === 0) return <span className="text-gray-400">-</span>
  if (seconds < 1) return <span>{Math.round(seconds * 1000)} ms</span>
  if (seconds < 60) return <span>{seconds.toFixed(2)} s</span>
  return <span>{(seconds / 60).toFixed(2)} min</span>
}

const LOG_STATUS_VARIANT: Record<string, PillVariant> = {
  success: 'success',
  error: 'danger',
  refund: 'warning',
  cancel: 'neutral',
  timeout: 'orange',
  retry: 'violet',
}

export function renderLogStatus(status?: string | null) {
  if (!status) return <span className="text-gray-400">-</span>
  const variant = LOG_STATUS_VARIANT[status] || 'neutral'
  return <Pill variant={variant}>{status}</Pill>
}

export function renderErrorStage(stage?: string | null) {
  if (!stage) return <span className="text-gray-400">-</span>
  return <Pill variant="neutral">{stage}</Pill>
}

export function renderDurationMs(ms?: number | null) {
  if (ms == null || ms <= 0) return <span className="text-gray-400">-</span>
  if (ms < 1000) return <span className="tabular-nums">{ms} ms</span>
  return <span className="tabular-nums">{(ms / 1000).toFixed(2)} s</span>
}

export function renderStreamMode(isStream?: boolean | null) {
  if (isStream == null) return <span className="text-gray-400">-</span>
  return (
    <Pill variant={isStream ? 'info' : 'neutral'}>
      {isStream ? i18n.t('tt.log.stream') : i18n.t('tt.log.nonStream')}
    </Pill>
  )
}

export function stampToDate(ts: number | undefined | null): string {
  if (!ts) return '-'
  return new Date(ts * 1000).toLocaleString(getDateLocale(), { hour12: false })
}

// ===================== Quota 进度条 =====================

export function renderQuotaWithUsage(remain: number, used: number) {
  const total = remain + used
  const percent = total > 0 ? Math.round((used / total) * 100) : 0
  const amountLabel = renderQuota(remain)
  return (
    <div className="flex items-center gap-1.5 w-full min-w-0 max-w-full">
      <span
        className="text-xs font-medium text-[#111827] tabular-nums shrink-0 truncate max-w-[30%] min-w-0"
        title={amountLabel}
      >
        {amountLabel}
      </span>
      <div className="flex-1 flex items-center gap-1.5 min-w-0">
        <Progress
          percent={percent}
          size="small"
          showText={false}
          className="!m-0 flex-1 min-w-0"
          color={percent > 80 ? '#EF4444' : percent > 50 ? '#F59E0B' : '#10B981'}
        />
        <span className="text-xs text-gray-500 tabular-nums w-8 text-right shrink-0">
          {percent}%
        </span>
      </div>
    </div>
  )
}

/** 剩余额度 / 总额度（列表列专用） */
export function renderQuotaRemainTotal(remain: number, used: number) {
  const total = remain + used
  return (
    <span className="text-xs tabular-nums text-[var(--console-text-body)] whitespace-nowrap">
      {renderQuotaCompact(remain)} / {renderQuotaCompact(total)}
    </span>
  )
}

/** 列表密钥遮罩展示 */
export function renderMaskedApiKey(key?: string) {
  const raw = (key || '').trim()
  if (!raw) {
    return <span className="text-xs text-[var(--console-text-subtle)]">sk-****</span>
  }
  const display = raw.startsWith('sk-') ? raw : `sk-${raw}`
  return (
    <span className="text-xs font-mono text-[var(--console-text-muted)] truncate block max-w-[140px]" title={display}>
      {display}
    </span>
  )
}

const MODEL_LIMITS_PREVIEW = 3

/** 可用模型列：未启用限制显示「全部可用模型」，否则 Tag 预览 + Popover */
export function renderModelLimits(enabled: boolean, models: string[] | undefined) {
  if (!enabled) {
    return <span className="text-xs text-[var(--console-text-muted)]">{i18n.t('tt.apiKey.allModels')}</span>
  }
  const list = (models || []).filter(Boolean)
  if (list.length === 0) {
    return <span className="text-xs text-[var(--console-text-subtle)]">-</span>
  }
  const preview = list.slice(0, MODEL_LIMITS_PREVIEW)
  const rest = list.length - preview.length
  const content = (
    <div className="flex flex-wrap items-center gap-1 max-w-[220px]">
      {preview.map((m) => (
        <Pill key={m} variant="info" className="!max-w-full truncate" title={m}>
          {m}
        </Pill>
      ))}
      {rest > 0 ? (
        <Pill variant="neutral" title={list.slice(MODEL_LIMITS_PREVIEW).join(', ')}>
          +{rest}
        </Pill>
      ) : null}
    </div>
  )
  if (list.length <= MODEL_LIMITS_PREVIEW) {
    return content
  }
  return (
    <Tooltip
      content={
        <ul className="max-w-xs max-h-48 overflow-y-auto text-xs space-y-0.5 py-0.5">
          {list.map((m) => (
            <li key={m} className="break-all">
              {m}
            </li>
          ))}
        </ul>
      }
    >
      {content}
    </Tooltip>
  )
}

// ===================== 异步任务状态 =====================

const TASK_STATUS_VARIANT: Record<string, PillVariant> = {
  SUCCESS: 'success',
  FAILURE: 'danger',
  IN_PROGRESS: 'cyan',
  SUBMITTED: 'info',
  QUEUED: 'warning',
  NOT_START: 'neutral',
}

const TASK_STATUS_I18N: Record<string, string> = {
  SUCCESS: 'tt.task.statusSuccess',
  FAILURE: 'tt.task.statusFailure',
  IN_PROGRESS: 'tt.task.statusInProgress',
  SUBMITTED: 'tt.task.statusSubmitted',
  QUEUED: 'tt.task.statusQueued',
  NOT_START: 'tt.task.statusNotStart',
}

export function renderTaskStatus(status?: string) {
  const key = (status || '').toUpperCase()
  const variant = TASK_STATUS_VARIANT[key] || 'neutral'
  const labelKey = TASK_STATUS_I18N[key]
  const label = labelKey ? i18n.t(labelKey) : status || '-'
  return <Pill variant={variant}>{label}</Pill>
}

// ===================== 原样透传 =====================

export function renderText(text?: string | number | null) {
  if (text === null || text === undefined || text === '') {
    return <span className="text-gray-400">-</span>
  }
  return <span>{String(text)}</span>
}
